import { useEffect, useMemo, useState } from "react";
import { Upload, Sparkles, Loader2, X, BookmarkCheck, ChevronDown, Zap, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { api, CATEGORIES, formatAmount, currencySymbol } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format as formatDate, parseISO } from "date-fns";

const SPLIT_TYPES = [
  { value: "equal", label: "Equal" },
  { value: "exact", label: "Exact" },
  { value: "percent", label: "%" },
  { value: "shares", label: "Shares" },
];

const emptyMap = (ids) => Object.fromEntries(ids.map((id) => [id, ""]));

export const BillDrawer = ({ open, onOpenChange, group, bill, onSaved }) => {
  const { user } = useAuth();
  const isEdit = !!bill;

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [dateStr, setDateStr] = useState(formatDate(new Date(), "yyyy-MM-dd"));
  const [category, setCategory] = useState("food");
  const [paidBy, setPaidBy] = useState("");
  const [splitType, setSplitType] = useState("equal");
  const [participants, setParticipants] = useState({});
  const [amounts, setAmounts] = useState({});
  const [percents, setPercents] = useState({});
  const [shares, setShares] = useState({});
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [quickMode, setQuickMode] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("stb_quick_add") !== "0";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("stb_quick_add", quickMode ? "1" : "0");
  }, [quickMode]);

  useEffect(() => {
    if (!open || !group || isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/split-templates?group_id=${group.id}`);
        setTemplates(data);
      } catch (e) { console.warn("Templates load failed:", e?.message); }
    })();
  }, [open, group, isEdit]);

  const applyTemplate = (tpl) => {
    setTitle(tpl.name);
    setCategory(tpl.category || "general");
    setSplitType(tpl.split_type);
    if (tpl.default_amount) setAmount(String(tpl.default_amount));
    const pMap = Object.fromEntries(memberIds.map((id) => [id, false]));
    const aMap = {}, pctMap = {}, sMap = {};
    for (const m of tpl.members) {
      if (memberIds.includes(m.user_id)) {
        pMap[m.user_id] = true;
        if (tpl.split_type === "exact") aMap[m.user_id] = String(m.value);
        if (tpl.split_type === "percent") pctMap[m.user_id] = String(m.value);
        if (tpl.split_type === "shares") sMap[m.user_id] = String(m.value);
      }
    }
    setParticipants(pMap);
    setAmounts(aMap);
    setPercents(pctMap);
    setShares(sMap);
    toast.success(`Template "${tpl.name}" applied`);
  };


  const memberIds = useMemo(() => (group?.members || []).map((m) => m.id), [group]);
  const sym = group ? currencySymbol(group.currency) : "";

  // Initialize form when drawer opens or bill changes
  useEffect(() => {
    if (!open || !group) return;
    if (isEdit && bill) {
      setTitle(bill.title || "");
      setDesc(bill.description || "");
      setAmount(String(bill.amount || ""));
      try {
        setDateStr(formatDate(parseISO(bill.date), "yyyy-MM-dd"));
      } catch {
        setDateStr(formatDate(new Date(), "yyyy-MM-dd"));
      }
      setCategory(bill.category || "food");
      setPaidBy(bill.paid_by);
      setSplitType(bill.split_type || "equal");
      const pMap = {}, aMap = {}, pctMap = {}, sMap = {};
      memberIds.forEach((id) => {
        const found = bill.splits.find((s) => s.user_id === id);
        pMap[id] = !!found;
        aMap[id] = found ? String(found.amount) : "";
        pctMap[id] = found?.meta?.percent != null ? String(found.meta.percent) : "";
        sMap[id] = found?.meta?.shares != null ? String(found.meta.shares) : "";
      });
      setParticipants(pMap);
      setAmounts(aMap);
      setPercents(pctMap);
      setShares(sMap);
    } else {
      setTitle("");
      setDesc("");
      setAmount("");
      setDateStr(formatDate(new Date(), "yyyy-MM-dd"));
      setCategory("food");
      setPaidBy(user.id);
      setSplitType("equal");
      const pMap = Object.fromEntries(memberIds.map((id) => [id, true]));
      setParticipants(pMap);
      setAmounts(emptyMap(memberIds));
      setPercents(emptyMap(memberIds));
      setShares(emptyMap(memberIds));
    }
  }, [open, bill, isEdit, group, memberIds, user.id]);

  const activeMembers = () => (group?.members || []).filter((m) => participants[m.id]);

  const computeSplits = () => {
    const amt = Number(amount || 0);
    if (!amt || !group) return [];
    if (splitType === "equal") {
      const active = activeMembers();
      if (active.length === 0) return [];
      const rounded = Math.round((amt / active.length) * 100) / 100;
      const diff = Math.round((amt - rounded * active.length) * 100) / 100;
      return active.map((m, idx) => ({
        user_id: m.id,
        amount: Math.round((rounded + (idx === 0 ? diff : 0)) * 100) / 100,
      }));
    }
    if (splitType === "exact") {
      return group.members
        .map((m) => ({ user_id: m.id, amount: Number(amounts[m.id] || 0) }))
        .filter((s) => s.amount > 0);
    }
    if (splitType === "percent") {
      const total = group.members.reduce((s, m) => s + Number(percents[m.id] || 0), 0);
      if (total === 0) return [];
      return group.members
        .filter((m) => Number(percents[m.id] || 0) > 0)
        .map((m) => ({
          user_id: m.id,
          amount: Math.round((amt * (Number(percents[m.id]) / total)) * 100) / 100,
          meta: { percent: Number(percents[m.id]) },
        }));
    }
    if (splitType === "shares") {
      const total = group.members.reduce((s, m) => s + Number(shares[m.id] || 0), 0);
      if (total === 0) return [];
      return group.members
        .filter((m) => Number(shares[m.id] || 0) > 0)
        .map((m) => ({
          user_id: m.id,
          amount: Math.round((amt * (Number(shares[m.id]) / total)) * 100) / 100,
          meta: { shares: Number(shares[m.id]) },
        }));
    }
    return [];
  };

  const splits = computeSplits();
  const splitTotal = splits.reduce((s, x) => s + x.amount, 0);
  const remaining = Math.round((Number(amount || 0) - splitTotal) * 100) / 100;

  // Validation: sum of splits must exactly equal total bill amount (< $0.01 tolerance for float math)
  const percentTotal = splitType === "percent"
    ? group.members.reduce((s, m) => s + Number(percents[m.id] || 0), 0)
    : 0;
  const percentOk = splitType !== "percent" || Math.abs(percentTotal - 100) < 0.01;
  const amountOk = Number(amount) > 0;
  const splitOk = splitType === "equal" ? amountOk : (amountOk && Math.abs(remaining) < 0.01);
  const canSave = amountOk && !!paidBy && splits.length > 0 && percentOk && splitOk;

  let validationMessage = "";
  if (!amountOk) validationMessage = "Enter an amount greater than 0";
  else if (!paidBy) validationMessage = "Pick who paid";
  else if (splitType === "percent" && !percentOk) {
    const diff = Math.round((100 - percentTotal) * 100) / 100;
    validationMessage = diff > 0
      ? `Percentages add to ${percentTotal}% — need ${diff}% more`
      : `Percentages add to ${percentTotal}% — remove ${Math.abs(diff)}%`;
  } else if (!splitOk && splitType !== "equal") {
    if (remaining > 0) validationMessage = `${formatAmount(remaining, group.currency)} unallocated — assign the rest`;
    else validationMessage = `Over by ${formatAmount(-remaining, group.currency)} — reduce splits`;
  }

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = String(reader.result).split(",")[1];
      try {
        const { data } = await api.post("/ocr/receipt", { image_base64: base64 });
        if (data.title) setTitle(data.title);
        if (data.amount) setAmount(String(data.amount));
        if (data.category && CATEGORIES.find((c) => c.value === data.category)) setCategory(data.category);
        if (data.date) {
          try { setDateStr(formatDate(parseISO(data.date), "yyyy-MM-dd")); } catch (e) { console.warn("OCR date parse failed:", e?.message); }
        }
        toast.success("Receipt scanned");
      } catch {
        toast.error("OCR failed — enter manually");
      } finally {
        setOcrLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const finalTitle = title || (quickMode ? "Untitled expense" : title);
    if (!canSave) {
      toast.error(validationMessage || "Fix the splits before saving");
      return;
    }
    if (!finalTitle) {
      toast.error("Add a name for the bill");
      return;
    }
    let finalSplits = splits;
    if (Math.abs(remaining) > 0 && Math.abs(remaining) < 0.05 && finalSplits.length > 0) {
      finalSplits = [...finalSplits];
      finalSplits[0] = { ...finalSplits[0], amount: Math.round((finalSplits[0].amount + remaining) * 100) / 100 };
    } else if (Math.abs(remaining) >= 0.05) {
      toast.error(`Splits off by ${formatAmount(remaining, group.currency)}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        group_id: group.id,
        title: finalTitle,
        description: desc || null,
        amount: Number(amount),
        currency: group.currency,
        category,
        paid_by: paidBy,
        date: new Date(dateStr).toISOString(),
        split_type: splitType,
        splits: finalSplits,
      };
      const { data } = isEdit
        ? await api.patch(`/bills/${bill.id}`, payload)
        : await api.post("/bills", payload);
      toast.success(isEdit ? "Bill updated ✓" : "Bill added ✓");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!group) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-card border-border p-0 overflow-y-auto scrollbar-thin"
        data-testid="bill-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border sticky top-0 bg-card/95 backdrop-blur-xl z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                / {isEdit ? "Edit bill" : quickMode ? "Quick add" : "New bill"}
              </div>
              <SheetTitle className="font-heading text-2xl tracking-tight truncate">
                {isEdit ? title || "Edit expense" : quickMode ? "Add fast" : "Add an expense"}
              </SheetTitle>
            </div>
            {!isEdit && (
              <button
                type="button"
                onClick={() => setQuickMode((v) => !v)}
                className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-foreground/[0.03] hover:bg-foreground/[0.08] text-xs font-mono uppercase tracking-wider"
                data-testid="toggle-quick-mode"
              >
                {quickMode ? (
                  <><Settings2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Detailed</>
                ) : (
                  <><Zap className="h-3.5 w-3.5 brand-text" strokeWidth={1.5} /> Quick</>
                )}
              </button>
            )}
          </div>
        </SheetHeader>

        <div className="px-6 py-6 space-y-5">
          {!isEdit && !quickMode && (
            <div className="rounded-xl border border-dashed border-border p-3 flex items-center justify-between gap-3" data-testid="ocr-zone">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center shrink-0">
                  {ocrLoading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="h-4 w-4 brand-text" strokeWidth={1.5} />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm">Scan receipt</div>
                  <div className="text-xs text-muted-foreground truncate">AI auto-fills the form</div>
                </div>
              </div>
              <label className="cursor-pointer shrink-0">
                <input type="file" accept="image/*" onChange={handleReceiptUpload} className="hidden" data-testid="ocr-file-input" />
                <span className="inline-flex items-center h-9 px-3 rounded-md border border-border bg-foreground/[0.03] hover:bg-foreground/[0.08] transition text-xs">
                  <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Upload
                </span>
              </label>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name{quickMode ? " (optional)" : ""}</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-muted border-border h-11 flex-1" placeholder={quickMode ? "Untitled expense" : "Dinner at Miss Margarita"} data-testid="bill-title-input" />
              {!isEdit && templates.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" className="border-border h-11 shrink-0" data-testid="use-template-btn">
                      <BookmarkCheck className="h-4 w-4 mr-1.5 brand-text" strokeWidth={1.5} /> Template
                      <ChevronDown className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card border-border w-64">
                    <DropdownMenuLabel className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Use pre-set</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-border" />
                    {templates.map((tpl) => (
                      <DropdownMenuItem key={tpl.id} onClick={() => applyTemplate(tpl)} className="cursor-pointer" data-testid={`template-${tpl.id}`}>
                        <div className="flex flex-col">
                          <span className="text-sm">{tpl.name}</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{tpl.split_type} · {tpl.members.length} people</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {!quickMode && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Date</Label>
                <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="mt-1.5 bg-muted border-border h-11 font-mono" data-testid="bill-date-input" />
              </div>
            )}
            <div className={quickMode ? "col-span-2" : ""}>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Amount</Label>
              <div className="mt-1.5 relative">
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-mono ${quickMode ? "text-lg brand-text" : "text-muted-foreground"}`}>{sym}</span>
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={`pl-8 bg-muted border-border font-mono ${quickMode ? "h-14 text-2xl" : "h-11"}`} placeholder="0.00" data-testid="bill-amount-input" autoFocus={quickMode} />
              </div>
            </div>
          </div>

          <div className={quickMode ? "" : "grid grid-cols-2 gap-4"}>
            {!quickMode && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="bill-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Paid by</Label>
              <Select value={paidBy} onValueChange={setPaidBy}>
                <SelectTrigger className="mt-1.5 bg-muted border-border h-11" data-testid="bill-paidby"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {group.members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.avatar_emoji || "🙂"} {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!quickMode && (
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Notes (optional)</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5 bg-muted border-border" rows={2} placeholder="Table of 4, tipped 18%…" data-testid="bill-notes-input" />
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Split mode</Label>
            <Tabs value={splitType} onValueChange={setSplitType}>
              <TabsList className="bg-muted border border-border w-full grid grid-cols-4" data-testid="split-type-tabs">
                {SPLIT_TYPES.map((s) => (
                  <TabsTrigger key={s.value} value={s.value} className="data-[state=active]:brand-bg data-[state=active]:text-black" data-testid={`split-tab-${s.value}`}>
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {quickMode && splitType === "equal" ? (
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground" data-testid="quick-split-hint">
              Split equally between all {group.members.length} members
              {amount ? ` · ${formatAmount(Number(amount) / group.members.length, group.currency)} each` : ""}
            </div>
          ) : (
          <div className="rounded-xl border border-border bg-muted/30 p-4" data-testid="split-editor">
            <div className="space-y-2">
              {group.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-md px-2 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {splitType === "equal" && (
                      <input
                        type="checkbox"
                        checked={!!participants[m.id]}
                        onChange={(e) => setParticipants({ ...participants, [m.id]: e.target.checked })}
                        className="h-4 w-4 accent-[var(--brand)]"
                        data-testid={`split-equal-${m.id}`}
                      />
                    )}
                    <span className="h-8 w-8 rounded-full bg-foreground/10 flex items-center justify-center text-sm shrink-0">{m.avatar_emoji || "🙂"}</span>
                    <span className="text-sm truncate">{m.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {splitType === "exact" && (
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{sym}</span>
                        <Input
                          type="number" step="0.01" value={amounts[m.id] ?? ""}
                          onChange={(e) => setAmounts({ ...amounts, [m.id]: e.target.value })}
                          className="w-28 h-9 text-sm pl-5 bg-background border-border font-mono"
                          data-testid={`split-exact-${m.id}`}
                        />
                      </div>
                    )}
                    {splitType === "percent" && (
                      <div className="relative">
                        <Input
                          type="number" step="0.1" value={percents[m.id] ?? ""}
                          onChange={(e) => setPercents({ ...percents, [m.id]: e.target.value })}
                          className="w-24 h-9 text-sm pr-6 bg-background border-border font-mono"
                          data-testid={`split-percent-${m.id}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                    {splitType === "shares" && (
                      <Input
                        type="number" step="1" value={shares[m.id] ?? ""}
                        onChange={(e) => setShares({ ...shares, [m.id]: e.target.value })}
                        className="w-20 h-9 text-sm bg-background border-border font-mono"
                        placeholder="0"
                        data-testid={`split-shares-${m.id}`}
                      />
                    )}
                    <span className="text-xs font-mono text-muted-foreground w-20 text-right" data-testid={`split-computed-${m.id}`}>
                      {(() => {
                        const s = splits.find((x) => x.user_id === m.id);
                        return s ? formatAmount(s.amount, group.currency) : "—";
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs font-mono uppercase tracking-wider">
              <span className="text-muted-foreground">
                {splitType === "percent" ? "Percentages" : "Remaining"}
              </span>
              {splitType === "percent" ? (
                <span
                  className={percentOk ? "brand-text" : "text-red-400"}
                  data-testid="split-remaining"
                >
                  {percentTotal.toFixed(1)}% / 100%
                </span>
              ) : (
                <span
                  className={Math.abs(remaining) < 0.01 ? "brand-text" : "text-red-400"}
                  data-testid="split-remaining"
                >
                  {Math.abs(remaining) < 0.01 ? `${sym}0.00` : `${remaining > 0 ? "+" : "-"}${sym}${Math.abs(remaining).toFixed(2)}`}
                </span>
              )}
            </div>
          </div>
          )}

          {isEdit && (
            <div className="rounded-lg bg-muted/50 border border-border p-3 text-[11px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="audit-log">
              Created by {(group.members.find((m) => m.id === bill.created_by)?.name) || "Unknown"}
              {bill.updated_by && bill.updated_by !== bill.created_by && (
                <> · Edited by {(group.members.find((m) => m.id === bill.updated_by)?.name) || "Unknown"}</>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border sticky bottom-0 bg-card/95 backdrop-blur-xl">
          {validationMessage && (
            <div className="mb-2 text-xs font-mono text-red-400" data-testid="bill-validation-msg">
              {validationMessage}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground" data-testid="bill-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !canSave}
              className="flex-1 brand-bg text-black hover:opacity-90 active:scale-[0.98] h-11 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bill-submit-btn"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add bill"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
